import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Set initial user ID from environment
let userId = process.env.REACT_APP_DEFAULT_USER_ID;
const authToken = localStorage.getItem('authToken');

if (authToken) {
  userId = authToken;
}

export const setUserId = (newUserId) => {
  userId = newUserId;
};

// Request interceptor to add user ID to headers
apiClient.interceptors.request.use((config) => {
  if (userId) {
    config.headers['user-Id'] = userId;
  }
  
  // Only set Content-Type to JSON if not already set to something else
  // (e.g. multipart/form-data for file uploads)
  if (!config.headers['Content-Type'] || 
      (config.headers['Content-Type'] === 'application/json' && !(config.data instanceof FormData))) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  // If data is FormData, let the browser set the Content-Type 
  // (it needs to include the boundary parameter)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  config.headers['Accept'] = 'application/json';

  return config;
});

apiClient.requestEndpoint = async (method, endpoint, data) => {
  const url = endpoint.startsWith('/api/') ? endpoint : `/api/${endpoint}/v1`;
  return apiClient({ method, url, data });
};

// Authentication
export const login = async (credentials) => {
  try {
    const response = await apiClient.post('/api/auth/login/v1/', credentials);
    const userData = response.data;

    setUserId(userData.data.token);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = async () => {
  // The interceptor will automatically add User-Id header
  const response = await apiClient.get('/api/auth/me/v1');
  return response.data;
};

// Users API
export const fetchUsers = async () => {
  return apiClient.get('/api/users/v1');
};

export const createUser = async (userData) => {
  try {
    const response = await apiClient.post('/api/users/v1', userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (id, userData) => {
  return apiClient.put(`/api/users/v1/${id}`, userData);
};

export const deleteUser = async (id) => {
  return apiClient.delete(`/api/users/v1/${id}`);
};

// Addresses API
export const fetchAddresses = async () => {
  return apiClient.get('/api/addresses/v1');
};

export const createAddress = async (addressData) => {
  return apiClient.post('/api/addresses/v1', addressData);
};

export const updateAddress = async (id, addressData) => {
  return apiClient.put(`/api/addresses/v1/${id}`, addressData);
};

export const deleteAddress = async (id) => {
  return apiClient.delete(`/api/addresses/v1/${id}`);
};

// Alerts API
export const fetchAlerts = async () => {
  return apiClient.get('/api/alerts/v1');
};

export const createAlert = async (alertData) => {
  return apiClient.post('/api/alerts/v1', alertData);
};

export const updateAlert = async (id, alertData) => {
  return apiClient.put(`/api/alerts/v1/${id}`, alertData);
};

export const deleteAlert = async (id) => {
  return apiClient.delete(`/api/alerts/v1/${id}`);
};

// Articles API
export const fetchArticles = async () => {
  return apiClient.get('/api/articles/v1');
};

export const createArticle = async (articleData) => {
  return apiClient.post('/api/articles/v1', articleData);
};

export const updateArticle = async (id, articleData) => {
  return apiClient.put(`/api/articles/v1/${id}`, articleData);
};

export const deleteArticle = async (id) => {
  return apiClient.delete(`/api/articles/v1/${id}`);
};

// Authors API
export const fetchAuthors = async () => {
  return apiClient.get('/api/authors/v1');
};

export const createAuthor = async (authorData) => {
  return apiClient.post('/api/authors/v1', authorData);
};

export const updateAuthor = async (id, authorData) => {
  return apiClient.put(`/api/authors/v1/${id}`, authorData);
};

export const deleteAuthor = async (id) => {
  return apiClient.delete(`/api/authors/v1/${id}`);
};

// Companies API
export const fetchCompanies = async () => {
  return apiClient.get('/api/companies/v1');
};

export const createCompany = async (companyData) => {
  return apiClient.post('/api/companies/v1', companyData);
};

export const updateCompany = async (id, companyData) => {
  return apiClient.put(`/api/companies/v1/${id}`, companyData);
};

export const deleteCompany = async (id) => {
  return apiClient.delete(`/api/companies/v1/${id}`);
};

// Company Addresses API
export const fetchCompanyAddresses = async () => {
  return apiClient.get('/api/company_addresses/v1');
};

export const createCompanyAddress = async (companyAddressData) => {
  return apiClient.post('/api/company_addresses/v1', companyAddressData);
};

export const updateCompanyAddress = async (id, companyAddressData) => {
  return apiClient.put(`/api/company_addresses/v1/${id}`, companyAddressData);
};

export const deleteCompanyAddress = async (id) => {
  return apiClient.delete(`/api/company_addresses/v1/${id}`);
};

// Configurations API
export const fetchConfigurations = async () => {
  return apiClient.get('/api/configurations/v1');
};

export const createConfiguration = async (configurationData) => {
  return apiClient.post('/api/configurations/v1', configurationData);
};

export const updateConfiguration = async (id, configurationData) => {
  return apiClient.put(`/api/configurations/v1/${id}`, configurationData);
};

export const deleteConfiguration = async (id) => {
  return apiClient.delete(`/api/configurations/v1/${id}`);
};

// Images API
export const fetchImages = async () => {
  return apiClient.get('/api/images/v1');
};

export const createImage = async (imageData) => {
  return apiClient.post('/api/images/v1', imageData);
};

export const updateImage = async (id, imageData) => {
  return apiClient.put(`/api/images/v1/${id}`, imageData);
};

export const deleteImage = async (id) => {
  return apiClient.delete(`/api/images/v1/${id}`);
};

// Keywords API
export const fetchKeywords = async () => {
  return apiClient.get('/api/keywords/v1');
};

export const createKeyword = async (keywordData) => {
  return apiClient.post('/api/keywords/v1', keywordData);
};

export const updateKeyword = async (id, keywordData) => {
  return apiClient.put(`/api/keywords/v1/${id}`, keywordData);
};

export const deleteKeyword = async (id) => {
  return apiClient.delete(`/api/keywords/v1/${id}`);
};

// Matches API
export const fetchMatches = async () => {
  return apiClient.get('/api/matches/v1');
};

export const getMatchById = async (id) => {
  try {
    const response = await apiClient.get(`/api/matches/v1/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch match');
  }
};

export const createMatch = async (matchData) => {
  return apiClient.post('/api/matches/v1', matchData);
};

export const updateMatch = async (id, matchData) => {
  return apiClient.put(`/api/matches/v1/${id}`, matchData);
};

export const deleteMatch = async (id) => {
  return apiClient.delete(`/api/matches/v1/${id}`);
};

// Pdffiles API
export const fetchPdffiles = async () => {
  return apiClient.get('/api/pdffiles/v1');
};

export const createPdffile = async (pdffileData) => {
  return apiClient.post('/api/pdffiles/v1', pdffileData);
};

export const updatePdffile = async (id, pdffileData) => {
  return apiClient.put(`/api/pdffiles/v1/${id}`, pdffileData);
};

export const deletePdffile = async (id) => {
  return apiClient.delete(`/api/pdffiles/v1/${id}`);
};

// Permissions API
export const fetchPermissions = async () => {
  return apiClient.get('/api/permissions/v1');
};

export const createPermission = async (permissionData) => {
  return apiClient.post('/api/permissions/v1', permissionData);
};

export const updatePermission = async (id, permissionData) => {
  return apiClient.put(`/api/permissions/v1/${id}`, permissionData);
};

export const deletePermission = async (id) => {
  return apiClient.delete(`/api/permissions/v1/${id}`);
};

// Role Permissions API
export const fetchRolePermission = async () => {
  return apiClient.get('/api/role_permissions/v1');
};

export const createRolePermission = async (rolePermissionData) => {
  return apiClient.post('/api/role_permissions/v1', rolePermissionData);
};

export const updateRolePermission = async (id, rolePermissionData) => {
  return apiClient.put(`/api/role_permissions/v1/${id}`, rolePermissionData);
};

export const deleteRolePermission = async (id) => {
  return apiClient.delete(`/api/role_permissions/v1/${id}`);
};

// Roles API
export const fetchRoles = async () => {
  return apiClient.get('/api/roles/v1');
};

export const createRole = async (roleData) => {
  return apiClient.post('/api/roles/v1', roleData);
};

export const updateRole = async (id, roleData) => {
  return apiClient.put(`/api/roles/v1/${id}`, roleData);
};

export const deleteRole = async (id) => {
  return apiClient.delete(`/api/roles/v1/${id}`);
};

// Sources API
export const fetchSources = async () => {
  return apiClient.get('/api/sources/v1');
};

export const createSource = async (sourceData) => {
  return apiClient.post('/api/sources/v1', sourceData);
};

export const updateSource = async (id, sourceData) => {
  return apiClient.put(`/api/sources/v1/${id}`, sourceData);
};

export const deleteSource = async (id) => {
  return apiClient.delete(`/api/sources/v1/${id}`);
};

// User Addresses API
export const fetchUserAddress = async () => {
  return apiClient.get('/api/user_addresses/v1');
};

export const createUserAddress = async (userAddressData) => {
  return apiClient.post('/api/user_addresses/v1', userAddressData);
};

export const updateUserAddress = async (id, userAddressData) => {
  return apiClient.put(`/api/user_addresses/v1/${id}`, userAddressData);
};

export const deleteUserAddress = async (id) => {
  return apiClient.delete(`/api/user_addresses/v1/${id}`);
};

// User Alert API
export const fetchUserAlert = async () => {
  return apiClient.get('/api/user_alerts/v1');
};

export const createUserAlert = async (userAlertData) => {
  return apiClient.post('/api/user_alerts/v1', userAlertData);
};

export const updateUserAlert = async (id, userAlertData) => {
  return apiClient.put(`/api/user_alerts/v1/${id}`, userAlertData);
};

export const deleteUserAlert = async (id) => {
  return apiClient.delete(`/api/user_alerts/v1/${id}`);
};

// User Role API
export const fetchUserRole = async () => {
  return apiClient.get('/api/user_roles/v1');
};

export const createUserRole = async (userRoleData) => {
  return apiClient.post('/api/user_roles/v1', userRoleData);
};

export const updateUserRole = async (id, userRoleData) => {
  return apiClient.put(`/api/user_roles/v1/${id}`, userRoleData);
};

export const deleteUserRole = async (id) => {
  return apiClient.delete(`/api/user_roles/v1/${id}`);
};

export default apiClient;